import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { Link, LinkType } from "~/common/components/Link";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useLockBodyScroll } from "~/common/hooks/useLockBodyScroll";
import classNames from "classnames";
import classes from "./SearchProductResults.module.less";
import type { SrpProductData } from "~/utils/models";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AutosuggestProductGroup } from "~/domains/navigation/types/NavigationTypes";
import { SEARCH_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "~/utils/url/urlResolver";
import { saveSearchTerm } from "~/features/search/api/saveSearchTerm";
import { TextWithHighlight } from "~/common/components/TextWithHighlight";
import { ProductItem } from "~/features/search/components/Autosuggest/SearchProductResults/ProductItem/ProductItem";
import { Skeleton } from "~/common/mui";

interface SearchProductResultsProps {
  className?: string | false;
  results?: SrpProductData[];
  groups?: AutosuggestProductGroup[];
  state: "loading" | "loaded" | "error";
  query: string;
  hide: () => void;
}

const MAX_PRODUCTS = 5;
const MAX_GROUPS = 3;

const SearchProductResults = ({ results, query, groups, state, hide, className }: SearchProductResultsProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  if (!isDesktop) {
    // TODO: Daniel check this
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLockBodyScroll();
  }

  const refList = useRef<HTMLAnchorElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex + 1) % refList.current.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex - 1 + refList.current.length) % refList.current.length);
      } else if (event.key === "Escape") {
        event.preventDefault();
        hide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refList]);

  useEffect(() => {
    if (focusedIndex !== -1) {
      refList.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  const resultsWithHighlights: (SrpProductData & { highlight: JSX.Element })[] | undefined = useMemo(() => {
    return results?.slice(0, MAX_PRODUCTS).map((item) => ({
      ...item,
      highlight: (
        <TextWithHighlight highlight={query} highlightClass={"f-bold"}>
          {item.designation}
        </TextWithHighlight>
      ),
    }));
  }, [results, query]);

  // const addToCartClick = (e: React.MouseEvent, productCode: string, restrictionQuery: string) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   closeModalById("add-to-cart-dialog");
  //   return AddToCartService.openAddToCartWizard({
  //     productCode: productCode,
  //     restrictionQuery: restrictionQuery,
  //   });
  // };

  return (
    <div className={classNames(className, classes.searchProductResultContainer)}>
      <div className={classes.header}>{t("header.autosuggest.col.product")}</div>
      <div className={classes.productContainer} data-zoovu={"searchSuggestions"}>
        {state === "loaded" ? (
          resultsWithHighlights?.map((item, index) => (
            <ProductItem
              key={index}
              product={item}
              query={query}
              hide={hide}
              ref={(el) => {
                if (el) {
                  refList.current[index] = el;
                }
              }}
            />
          ))
        ) : (
          <Skeleton variant="rectangular" />
        )}
      </div>

      {groups && groups.length > 0 && (
        <div className={classes.productGroupsContainer}>
          <span className={classes.productGroupsTitle}>{t("searchProduct.productGroups")}</span>
          {groups?.slice(0, MAX_GROUPS).map((group) => (
            <Link
              ref={(el) => {
                if (el && !refList.current.includes(el)) {
                  refList.current.push(el);
                }
              }}
              type="button"
              className={classes.productGroupLink}
              variant={LinkType.NAVIGATION}
              onClick={() => saveSearchTerm(group.name ?? "", group.url)}
              href={resolveUrl(`${group.url}`)}
            >
              {group.name}
            </Link>
          ))}
        </div>
      )}
      <div className={classes.totalResults}>
        <Link
          href={SEARCH_PAGE + `?text=${query}`}
          iconBefore={<Icon type={IconType.Search} />}
          tracking-aux-sch-show-total-results-link="true"
        >
          {t("searchRelated.showAll")}
        </Link>
      </div>
    </div>
  );
};

export default SearchProductResults;
